import { PlusOutlined } from '@ant-design/icons';
import { Button, Col, Form, Row, Select, Table, Tooltip } from 'antd';
import { ColumnsType } from 'antd/es/table';
import Search from 'antd/es/input/Search';
import { TablePaginationConfig } from 'antd/lib';
import { Dayjs } from 'dayjs';
import React, { useContext, useEffect, useState } from 'react';
import { FormattedDate, FormattedMessage, FormattedNumber, useIntl } from 'react-intl';
import { Link } from 'react-router-dom';
import plotApi from '../../apis/PlotApi';
import treatmentApi from '../../apis/TreatmentApi';
import ConstantLabel from '../../components/ConstantLabel/ConstantLabel';
import PhytosanitaryProductComponent from '../../components/PhytosanitaryProductComponent/PhytosanitaryProductComponent';
import LayoutComponent from '../../components/LayoutComponent/LayoutComponent';
import useResponsiveLayout from '../../components/LayoutComponent/UseResponsiveLayout/UseResponsiveLayout';
import CustomContext from '../../contexts/CustomContext';
import { Page } from '../../models/Elements';
import { CustomAuth, Plot, Treatment } from '../../models/Entities';
import { CropType, crops } from '../../models/Types';
import alertService from '../../services/AlertService';
import stringService from '../../services/StringService';
import paginationService from '../../services/TableService';
import styles from './TreatmentsPage.module.scss';

/**
 * Returns the treatments page that lists all the treatments of the organization.
 * @returns the treatments page.
 */
const TreatmentsPage: React.FC = () => {
    /*** HOOKS ***/

    const intl = useIntl();
    const [desktop] = useResponsiveLayout();
    const [form] = Form.useForm();
    const context = useContext(CustomContext);
    const auth = context.auth as CustomAuth;
    const companyId = auth.companyId as number;
    const [plots, setPlots] = useState<Plot[]>([]);
    const [treatmentsPage, setTreatmentsPage] = useState<Page<Treatment>>();
    const [page, setPage] = useState<number>(0);
    const [filter, setFilter] = useState<Filter>({});
    const [sortField, setSortField] = useState<string>('startDate');
    const [sortOrder, setSortOrder] = useState<boolean>(false);
    const [loading, setLoading] = useState<'loading'>();

    /*** EFFECTS ***/

    // list plots
    useEffect(() => {
        const init = async () => {
            try {
                const plotsPage = await plotApi.list(0, 1000, 'name', true, companyId);
                setPlots(plotsPage.content);
            } catch (error) {
                alertService.displayError(error, intl);
            }
        };
        init();
    }, [intl, companyId]);

    // list treatments
    useEffect(() => {
        const init = async () => {
            try {
                setLoading('loading');
                const treatmentsPage = await treatmentApi.list(
                    page,
                    paginationService.pageSize,
                    sortField,
                    sortOrder,
                    filter.plotId,
                    filter.crop,
                    filter.searchText
                );
                setTreatmentsPage(treatmentsPage);
            } catch (error) {
                alertService.displayError(error, intl);
            } finally {
                setLoading(undefined);
            }
        };
        init();
    }, [intl, page, sortField, sortOrder, filter.plotId, filter.crop, filter.searchText, companyId]);

    /*** METHODS ***/

    const handleTableChange = async (pagination: TablePaginationConfig, filters: any, sorter: any) => {
        setPage(pagination.current ? pagination.current - 1 : 0);
        sorter.field !== undefined && setSortField(sorter.field);
        setSortOrder(sorter.order === 'ascend');
    };

    const filterTreatments = async (values: any) => {
        const filter: Filter = {
            plotId: values.plotId,
            crop: values.crop,
            searchText: values.searchText
        };
        setFilter(filter);
        setPage(0);
    };

    /*** VISUAL ***/

    const plotOptions = plots.map((plot) => (
        <Select.Option key={plot.id} value={plot.id}>
            {plot.name}
        </Select.Option>
    ));

    const cropOptions = crops.map((crop) => (
        <Select.Option key={crop} value={crop}>
            {intl.formatMessage({ id: `crop.type.${crop}` })}
        </Select.Option>
    ));

    const plotsById = new Map<number, Plot>(plots.filter((plot) => plot.id !== undefined).map((plot) => [plot.id!, plot]));
    const items = treatmentsPage ? treatmentsPage.content : [];
    const columns: ColumnsType<Treatment> = [
        {
            title: <FormattedMessage id="treatment.startDate" />,
            dataIndex: 'startDate',
            key: 'startDate',
            width: 130,
            align: 'center',
            sorter: true,
            defaultSortOrder: 'descend',
            render: (value: Dayjs, treatment: Treatment) => (
                <Link to={`/treatments/${treatment.id}`}>{value && <FormattedDate value={value as any} year="numeric" month="2-digit" day="2-digit" />}</Link>
            )
        },
        {
            title: <FormattedMessage id="treatment.productName" />,
            dataIndex: 'productName',
            key: 'productName',
            sorter: true,
            render: (value: string, treatment: Treatment) => (
                <>
                    <PhytosanitaryProductComponent registerNumber={treatment.productRegisterNumber} />
                    <Link to={`/treatments/${treatment.id}`}>{value}</Link>
                </>
            )
        },
        {
            title: <FormattedMessage id="treatment.productRegisterNumber" />,
            dataIndex: 'productRegisterNumber',
            key: 'productRegisterNumber',
            width: 120,
            align: 'right',
            sorter: true,
            render: (value: number | undefined, treatment: Treatment) => (
                <Link to={`/treatments/${treatment.id}`}>{value !== undefined && <FormattedNumber value={value} useGrouping={false} />}</Link>
            )
        },
        {
            title: <FormattedMessage id="treatment.dose" />,
            dataIndex: 'dose',
            key: 'dose',
            width: 90,
            align: 'right',
            sorter: true,
            render: (value: number | undefined, treatment: Treatment) => (
                <Link to={`/treatments/${treatment.id}`}>
                    {value !== undefined && (
                        <>
                            <FormattedNumber value={value} minimumFractionDigits={0} maximumFractionDigits={2} />
                            {treatment.doseUnit && (
                                <>
                                    {' '}
                                    <ConstantLabel value={treatment.doseUnit} prefix="treatment.doseUnit." />
                                </>
                            )}
                        </>
                    )}
                </Link>
            )
        },
        {
            title: <FormattedMessage id="treatment.crop" />,
            dataIndex: 'crop',
            key: 'crop',
            width: 120,
            render: (value: string | undefined, treatment: Treatment) => (
                <Link to={`/treatments/${treatment.id}`}>
                    <ConstantLabel value={value} prefix="crop.type." />
                </Link>
            )
        },
        {
            title: <FormattedMessage id="treatment.parcel" />,
            dataIndex: 'parcel',
            key: 'parcel',
            width: 120,
            sorter: true,
            render: (value: string, treatment: Treatment) => <Link to={`/treatments/${treatment.id}`}>{value}</Link>
        },
        {
            title: <FormattedMessage id="treatment.area.short" />,
            dataIndex: 'area',
            key: 'area',
            width: 110,
            align: 'right',
            sorter: true,
            render: (value: number | undefined, treatment: Treatment) => (
                <Link to={`/treatments/${treatment.id}`}>
                    {value !== undefined && <FormattedNumber value={value} minimumFractionDigits={0} maximumFractionDigits={2} />}
                </Link>
            )
        },
        {
            title: <FormattedMessage id="treatment.plot" />,
            dataIndex: 'plotId',
            key: 'plotId',
            width: 160,
            render: (value: number | undefined, treatment: Treatment) => (
                <Link to={`/treatments/${treatment.id}`}>{value !== undefined && plotsById.get(value)?.name}</Link>
            )
        }
    ];

    return (
        <LayoutComponent
            title={<FormattedMessage id="treatments.title" />}
            menu="treatments"
            path={[{ path: '/treatments', name: <FormattedMessage id="treatments.title" /> }]}
        >
            <Form form={form} onFinish={filterTreatments} colon={false} layout="vertical" requiredMark={false}>
                <Row gutter={[24, 24]}>
                    <Col span={24} lg={9}>
                        <Form.Item name="searchText">
                            <Search
                                placeholder={intl.formatMessage({ id: 'treatments.search' })}
                                size="large"
                                className={styles.select}
                                allowClear
                                onSearch={form.submit}
                            />
                        </Form.Item>
                    </Col>
                    <Col span={24} lg={5}>
                        <Form.Item name="plotId">
                            <Select
                                size="large"
                                showSearch
                                filterOption={stringService.filterOptions}
                                placeholder={<FormattedMessage id="treatment.plot" />}
                                className={styles.select}
                                onSelect={form.submit}
                                onClear={form.submit}
                                allowClear
                            >
                                {plotOptions}
                            </Select>
                        </Form.Item>
                    </Col>
                    <Col span={24} lg={5}>
                        <Form.Item name="crop">
                            <Select
                                size="large"
                                showSearch
                                filterOption={stringService.filterOptions}
                                placeholder={<FormattedMessage id="treatment.crop" />}
                                className={styles.select}
                                onSelect={form.submit}
                                onClear={form.submit}
                                allowClear
                            >
                                {cropOptions}
                            </Select>
                        </Form.Item>
                    </Col>
                    <Col span={24} lg={5} className="table-buttons">
                        <Link to="/treatments/new">
                            <Tooltip title={<FormattedMessage id="treatments.new.tooltip" />}>
                                <Button type="primary" size="large" icon={<PlusOutlined />}>
                                    {desktop && <FormattedMessage id="button.add" tagName="span" />}
                                </Button>
                            </Tooltip>
                        </Link>
                    </Col>
                </Row>
            </Form>
            <Table
                dataSource={items}
                columns={columns}
                pagination={paginationService.createPagination(treatmentsPage!)}
                rowKey="id"
                onChange={handleTableChange}
                sortDirections={['ascend', 'descend']}
                showSorterTooltip={false}
                loading={loading === 'loading'}
                className="table"
            />
        </LayoutComponent>
    );
};
export default TreatmentsPage;

interface Filter {
    plotId?: number | undefined;
    crop?: CropType | undefined;
    searchText?: string | undefined;
}
